// RangeField: a labelled native slider for a whole number (DESIGN §7.3 — Batch no. / Month no.).
// Label left, the value large and right-aligned with its unit ("Batch no. 6 batch", "7 months"), then a helper line and
// an optional inline warning. The big value is itself a number input (click, type 30, Enter/Tab) so an exact number never
// needs dragging; the slider stays for coarse moves (arrow keys / Page Up/Down / Home / End come with the native control).
import './range-field.css';
import { useId, useState, useEffect } from 'preact/hooks';
import { cx } from './ui.jsx';

const clamp = (n, min, max) => Math.min(max, Math.max(min, Math.round(Number(n) || min)));

// unit: [singular, plural] (e.g. ['month', 'months']). onValue(number) on every move / valid keystroke.
export function RangeField({ label, name, value, onValue, min = 1, max = 99, unit = ['', ''], helper, warning, class: cls }) {
  const id = useId();
  const n = clamp(value, min, max);
  const [draft, setDraft] = useState(null); // text while typing in the big number
  useEffect(() => { setDraft(null); }, [n]);
  const word = n === 1 ? unit[0] : unit[1];
  const pct = max > min ? ((n - min) / (max - min)) * 100 : 0;
  const describedBy = [helper && `${id}-help`, warning && `${id}-warn`].filter(Boolean).join(' ') || undefined;
  const type = (text) => {
    const digits = text.replace(/\D/g, '').slice(0, String(max).length);
    setDraft(digits);
    const v = Number(digits);
    if (digits && v >= min && v <= max && v !== n) onValue?.(v);
  };
  const commit = () => { setDraft(null); };
  return (
    <div class={cx('rf', cls)}>
      <div class="rf-head">
        <label class="rf-label" for={id}>{label}</label>
        <span class="rf-value">
          <input
            class="rf-num"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            aria-label={`${label} (type a number)`}
            size={String(max).length}
            value={draft ?? String(n)}
            onInput={(e) => type(e.currentTarget.value)}
            onFocus={(e) => e.currentTarget.select()}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); e.currentTarget.blur(); }
              else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                onValue?.(clamp(n + (e.key === 'ArrowUp' ? 1 : -1), min, max));
              }
            }}
          />
          {word && <span class="rf-unit" aria-hidden="true">{word}</span>}
        </span>
      </div>
      <input
        id={id}
        name={name}
        class="rf-input"
        type="range"
        min={min}
        max={max}
        step={1}
        value={n}
        style={{ '--rf-pct': `${pct}%` }}
        aria-valuetext={word ? `${n} ${word}` : String(n)}
        aria-describedby={describedBy}
        onInput={(e) => onValue?.(Number(e.currentTarget.value))}
      />
      {helper && <p class="rf-help" id={`${id}-help`}>{helper}</p>}
      {warning && <p class="rf-warn" id={`${id}-warn`} role="status">{warning}</p>}
    </div>
  );
}

// Two sliders side by side on wide sheets, stacked on phones.
export function RangePair({ children }) {
  return <div class="rf-pair">{children}</div>;
}
